<template>
	<div
		:id="uid"
		class="field"
		:class="{
			'is-disabled': disabled,
			'is-required': required,
			'has-error': showError
		}"
	>
		<Label
			:for="`${uid}_input`"
			class="field__label"
			v-if="label"
			:labelText="label"
		/>
		<div class="field__input-container">
			<input
				class="field__input"
				autocomplete="off"
				ref="fieldinput"
				:aria-label="ariaLabel"
				:class="className"
				:id="`${uid}_input`"
				:min="min"
				:max="max"
				:name="name"
				:placeholder="placeholder"
				:required="required"
				:type="inputType"
				:value="dataValue"
				@input="updateValue"
				@change="updateValue"
				@keydown.enter="enterKeyDown"
				@keyup.enter="enterKeyUp"
				@keydown.down="downKeyPressed"
				@keydown.up="upKeyPressed"
				@click="toggleSuggestions"
				@focus="$v.dataValue.$touch()"
			/>
			<div class="field__trailing-container">
				<div class="field__trailing-item" v-if="showError">
					<div class="field__icon field__icon--error">
						<i class="icon icon-error-solid"></i>
					</div>
				</div>
				<div
					:class="[
						'field__trailing-item',
						hasSpinnerLoading && 'has-spinner'
					]"
					v-if="showInputIconButton"
				>
					<Button
						:btnClick="clearValue"
						:className="'btn--icon-only'"
						:iconClassName="iconClass"
						:iconBeforeLabel="true"
					/>
				</div>
			</div>
		</div>
		<div class="field__text field__text--error" v-if="showFieldError">
			{{ error }}
		</div>
		<HelpFeedback
			:helpText="helpText"
			v-if="helpText"
			data-test="helpFeedback"
		/>

		<DropdownList
			ref="dropdownlist"
			:autosuggestion="false"
			:id="`${uid}_ddl`"
			:isOpen="open"
			:options="matches"
			:value="dataValue"
			v-if="hasAutosuggestion && matches.length"
			@change="closeSuggestion"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/label/label';
import Button from '@/components/forms/button/button';
import DropdownList from '@/components/forms/dropdown/dropdown-list';
import DocumentClickedMixin from '@/mixins/document-click';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';

import {
	required,
	minLength,
	maxLength,
	minValue,
	maxValue,
	email,
	url,
	numeric,
	integer,
	alpha,
	alphaNum
} from 'vuelidate/lib/validators';
import _ from 'lodash';

export default {
	name: 'Fieldinput',
	mixins: [DocumentClickedMixin('closeSuggestion', 'hasDropdownList')],
	components: {
		Label,
		Button,
		DropdownList,
		HelpFeedback
	},
	model: {
		prop: 'value',
		event: 'input'
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		className: {
			type: String,
			default: ''
		},
		helpText: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		inputType: {
			type: String,
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: false
		},
		value: {
			type: [String, Number],
			default: ''
		},
		maxLength: {
			type: Number,
			default: 100
		},
		minLength: {
			type: Number,
			default: 0
		},
		max: {
			type: Number,
			default: Number.MAX_VALUE
		},
		min: {
			type: Number,
			default: 0
		},
		suggestions: {
			type: Array,
			default: () => []
		},
		restrictedValues: {
			type: Array,
			default: () => []
		},
		matchingValueMustBeExisting: {
			type: Boolean,
			default: false
		},
		timeout: {
			type: Number,
			default: 0
		},
		errorText: {
			type: String,
			default: ''
		},
		minValue: {
			type: Number
		},
		maxValue: {
			type: Number
		},
		forceCustomErrorMessage: {
			type: String,
			default: ''
		},
		isNegativeAllow: {
			type: Boolean,
			default: true
		},
		showFieldTextError: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			hasSpinnerLoading: false, //// DEV: this shoud be set to true when the button spinner is used ///
			currentItem: -1,
			hasMatching: false,
			matches: [],
			open: false,
			dataValue: ''
		};
	},
	computed: {
		hasDropdownList() {
			return this.hasAutosuggestion || this.hasRestrictedValues;
		},
		hasAutosuggestion() {
			return this.suggestions.length > 0;
		},
		hasRestrictedValues() {
			return this.restrictedValues.length > 0;
		},
		showInputIconButton() {
			return (
				this.inputType.toLowerCase() === 'password' ||
				(this.hasAutosuggestion && this.value)
			);
		},
		iconClass() {
			if (this.inputType.toLowerCase() === 'password') return 'icon-show';
			return 'icon-close';
		},
		showError() {
			return !!this.error;
		},
		showFieldError() {
			return this.showError && this.showFieldTextError;
		},
		error() {
			if (this.forceCustomErrorMessage)
				return this.forceCustomErrorMessage;

			if (_.get(this.$v, 'dataValue.$anyError', false)) {
				const value = this.$v.dataValue;
				let msg = '';

				if (value.required === false) {
					msg = 'is required';
				} else if (value.minLength === false) {
					msg = `should have at least ${this.minLength}`;
				} else if (value.maxLength === false) {
					msg = `should have no more than ${this.maxLength}`;
				} else if (value.minValue === false) {
					msg = `should have at least ${this.minValue}`;
				} else if (value.maxValue === false) {
					msg = `should have no more than ${this.maxValue}`;
				} else if (value.email === false) {
					msg = 'is not in a valid format';
				} else if (value.alpha === false) {
					msg = 'accepts only alphabet characters';
				} else if (value.alphaNum === false) {
					msg = 'accepts only alphanumerics';
				} else if (value.numeric === false) {
					msg = 'accepts only numerics';
				} else if (value.integer === false) {
					msg = 'accepts positive and negative integers.';
				} else if (value.url === false) {
					msg = 'is not a valid Url';
				}

				return `${this.label || 'Field'} ${msg}`;
				/*

				case this.hasRestrictedValues:
					handlingMsg(
						this.matchingRestrictedValues(val) ? this.errorText : ''
					);
					break;
				*/
			}

			return '';
		}
	},
	validations() {
		let result = {};
		if (this.required) {
			result.dataValue = {
				required
			};
		}
		if (this.minLength) {
			result.dataValue = {
				...result.dataValue,
				minLength: minLength(this.minLength)
			};
		}
		if (this.maxLength) {
			result.dataValue = {
				...result.dataValue,
				maxLength: maxLength(this.maxLength)
			};
		}
		if (this.maxValue) {
			result.dataValue = {
				...result.dataValue,
				maxValue: maxValue(this.maxValue)
			};
		}
		if (this.minValue || this.minValue === 0) {
			result.dataValue = {
				...result.dataValue,
				minValue: minValue(this.minValue)
			};
		}
		if (this.inputType.toLowerCase() === 'numeric') {
			result.dataValue = {
				...result.dataValue,
				numeric
			};
		}
		if (this.inputType.toLowerCase() === 'integer') {
			result.dataValue = {
				...result.dataValue,
				integer
			};
		}
		if (this.inputType.toLowerCase() === 'email') {
			result.dataValue = {
				...result.dataValue,
				email
			};
		}
		if (this.inputType.toLowerCase() === 'url') {
			result.dataValue = {
				...result.dataValue,
				url
			};
		}
		if (this.inputType.toLowerCase() === 'alpha') {
			result.dataValue = {
				...result.dataValue,
				alpha
			};
		}
		if (this.inputType.toLowerCase() === 'alphaNum') {
			result.dataValue = {
				...result.dataValue,
				alphaNum
			};
		}
		return { ...result };
	},
	methods: {
		clearValue() {
			if (this.hasAutosuggestion) this.emitEvent('');
		},
		closeSuggestion(val) {
			if (val) this.emitEvent(val);
			this.open = false;
		},
		downKeyPressed() {
			if (this.hasAutosuggestion) {
				this.open = true;
				if (this.currentItem < this.matches.length - 1)
					this.currentItem++;
			}
		},
		emitEvent(value, eventName) {
			eventName = eventName || 'input';
			if (
				this.inputType.toLowerCase() === 'number' &&
				value &&
				eventName === 'input'
			) {
				value = Number(value);

				if (!this.isNegativeAllow && Math.sign(value) === -1) {
					value = Math.abs(value);
				}
			}
			if (this.timeout > 0) {
				//delay the @keyup event
				if (this.timer) {
					clearTimeout(this.timer);
					this.timer = null;
				}
				const self = this;
				this.timer = setTimeout(() => {
					self.$emit(eventName, value);
				}, self.timeout);
				return;
			}

			this.$emit(eventName, value);
		},
		enterKeyDown() {
			if (this.hasAutosuggestion) {
				const obj = this.matches[this.currentItem];
				if (JSON.stringify(obj) !== '{}') {
					this.closeSuggestion(obj.value);
				}
			}
		},
		enterKeyUp(e) {
			this.$emit('enterKeyUp', e);
		},
		matchingValues(val) {
			const mapping = items =>
				items.map(item => {
					return {
						text: item.toString(),
						value: item
					};
				});

			if (val) {
				const internalMatches = this.suggestions.filter(x => {
					return x.toLowerCase().includes(val.toLowerCase());
				});
				const matchesLength = internalMatches.length;
				this.hasMatching =
					matchesLength === 1 && internalMatches[0] === val;

				if (matchesLength) {
					this.matches = mapping(internalMatches);
					return;
				}

				//no results
				this.matches = [];
				return;
			}

			this.matches = mapping(this.suggestions);
		},
		matchingRestrictedValues(val) {
			if (val) {
				const internalMatches = this.restrictedValues.filter(x => {
					return x.toLowerCase() === val.toLowerCase();
				});
				return internalMatches.length > 1;
			}
		},
		toggleSuggestions() {
			if (this.hasAutosuggestion) {
				this.open = !this.open;
				if (this.open) {
					this.matchingValues(
						(this.dataValue || '').toString().trim()
					);
				}
			} else {
				this.emitEvent(true, 'searchTextClicked');
			}
		},
		upKeyPressed() {
			if (this.hasAutosuggestion) {
				if (this.currentItem > 0) this.currentItem--;
			}
		},
		updateValue(e) {
			if (this.hasAutosuggestion) {
				this.open = true;
				this.currentItem = -1;
			}

			this.emitEvent(e.target.value);
		}
	},
	watch: {
		error: {
			immediate: true,
			handler(val) {
				this.emitEvent({ [this.name]: !!val }, 'hasError');
			}
		},
		value: {
			immediate: true,
			handler(val) {
				this.dataValue = val;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
	* Field Input styles
	* Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.field {
	position: relative;

	&__input-container {
		position: relative;
		@include textfield;
		display: flex;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__input {
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $input-padding-y $input-padding-x;
		width: 100%;
		background-color: transparent;
	}

	&__trailing-container {
		display: flex;
		align-items: center;
		font-size: $input-font-size + 2;

		.field__trailing-item.has-spinner {
			.btn--icon-only {
				min-width: auto;
				margin-right: $gutter-sm;
			}
		}
	}

	&__icon {
		padding-right: $input-padding-x;
	}

	&__icon--error {
		display: none;
	}
}


/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.field__input-container {
		border-color: $ui-negative;
	}

	.field__icon--error{
		display: block;
		color: $ui-negative;
	}
	.field__text--error {
		display: block;
		font-size: $label-font-size - 2;
		margin-top: $gutter-sm;
		color: $ui-negative;
	}
}
</style>
